import mapboxgl, { LngLatBoundsLike, MapboxOptions } from 'mapbox-gl'
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { DefaultConfig } from './config'
import 'mapbox-gl/dist/mapbox-gl.css'
import './overwrites.module.css'
import { InteractiveToggle } from './InteractiveToggle'
import { useWindowSize } from 'usehooks-ts'

export const MapBoxMap = ({
  language,
  config,
  dataSource = '',
  panAllowed = false,
  maxExtendBounds,
  viewBounds,
  getMap,
  init,
  style,
  dataYear,
}: {
  language: 'de' | 'fr'
  config?: Partial<MapboxOptions>
  panAllowed?: boolean
  dataSource?: string
  maxExtendBounds?: number[][]
  viewBounds?: number[][]
  getMap?: (map: mapboxgl.Map) => void
  init?: (map: mapboxgl.Map) => void
  style?: CSSProperties
  dataYear?: number
}) => {
  const [map, setMap] = useState<mapboxgl.Map>()
  const [isActive, setActive] = useState(false)
  const size = useWindowSize()
  const mapref = useRef(null)
  const [navControl, setNavControl] = useState<mapboxgl.NavigationControl>(
    new mapboxgl.NavigationControl()
  )

  useEffect(() => {
    if (map) return
    console.log('--- MAPLOAD ---')
    let m = new mapboxgl.Map({
      ...DefaultConfig,
      ...config,
      container: mapref.current,
    }).addControl(
      new mapboxgl.AttributionControl({
        customAttribution: '<b>' + dataSource + '</b>',
      })
    )
    setMap(m)
  }, [])
  const fitBounds = (bounds: number[][] | undefined) => {
    if (bounds && map && bounds.length === 2 && 
        bounds[0].length === 2 && bounds[1].length === 2) {
      if (config && config.pitch) map.setPitch(config.pitch)
      map.resize()
      // Explicitly format as LngLatBoundsLike
      const formattedBounds: LngLatBoundsLike = [
        [bounds[0][0], bounds[0][1]],
        [bounds[1][0], bounds[1][1]]
      ];
      map.fitBounds(formattedBounds, {
        padding: 0,
        duration: 150,
      })
    }
  }
  
  // Similarly for setMaxBounds
  if (maxExtendBounds && maxExtendBounds.length === 2 && 
      maxExtendBounds[0].length === 2 && maxExtendBounds[1].length === 2) {
    const formattedMaxBounds: LngLatBoundsLike = [
      [maxExtendBounds[0][0], maxExtendBounds[0][1]],
      [maxExtendBounds[1][0], maxExtendBounds[1][1]]
    ];
    map.setMaxBounds(formattedMaxBounds);
  }

  useEffect(() => {
    if (!map) return
    fitBounds(viewBounds)
    map.setMaxBounds(maxExtendBounds as LngLatBoundsLike)
    if (getMap) getMap(map)
  }, [size, viewBounds, maxExtendBounds, isActive])

  useEffect(() => {
    if (!map) return
    if (isActive) {
      setInteractive(true)
    }
    if (!isActive) {
      setInteractive(false)
    }
    if (getMap) getMap(map)
  }, [maxExtendBounds])

  useEffect(() => {
    if (!map) return
    if (isActive) {
      setInteractive(true)
    }
    if (!isActive) {
      setInteractive(false)
      mapref.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isActive])

  useEffect(() => {
    if (!map) return
    setInteractive(false)
    if (init) init(map)
  }, [map])

  const setInteractive = (value: boolean) => {
    if (!map) return
    if (value === true) {
      // map.boxZoom.enable()
      map.scrollZoom.enable()
      map.keyboard.enable()
      map.doubleClickZoom.enable()
      map.touchZoomRotate.enable()
      map.dragPan.enable()

      if (panAllowed) {
        map.dragRotate.enable()
      }
      map.dragPan.enable()
      map.addControl(navControl, 'top-right')
    }
    if (!value) {
      map.boxZoom.disable()
      map.scrollZoom.disable()
      map.dragPan.disable()
      map.dragRotate.disable()
      map.keyboard.disable()
      map.doubleClickZoom.disable()
      map.touchZoomRotate.disable()
      map.removeControl(navControl)
    }
  }
  return (
    <Sizer isActive={isActive}>
      <_MapBoxMap isActive={isActive} ref={mapref} style={style}>
        <InteractiveToggle change={setActive} language={language} />
        {dataYear === 2025 && (
          <ColorKey isActive={isActive}>
            <ColorKeyEntry style={{marginRight: '10px'}}>
              <Circle style={{ backgroundColor: 'var(--yellow-main)' }} />
              {language === 'fr' ? 'avant ' : 'vor '} 2024
            </ColorKeyEntry>
            <ColorKeyEntry >
              <Circle style={{ backgroundColor: 'var(--orange-main)' }} />
              2024
            </ColorKeyEntry>
          </ColorKey>
        )}
      </_MapBoxMap>
    </Sizer>
  )
}

const ColorKey = styled.div<{ isActive: boolean }>`
  font-size: 15px;
  position: absolute;
  top: 60px;
  right: ${(props) => (props.isActive ? '10px' : '10px')};
  z-index: 1000;
  display: flex;
  color: var(--text-color);
  padding:10px;
background-color: var(--site-background);
  @media only screen and (max-width: 711px) {
    right: ${(props) => (props.isActive ? '20px' : '20px')};  }
`
const ColorKeyEntry = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-color);
  &:last-of-type {
    margin-right: 0;
  }
`
const Circle = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 5px;
  display: inline-block;
`

const Sizer = styled.div<{ isActive: boolean }>`
  width: 100%;
  /* margin-top: 60px; */

  padding-bottom: 80vh;
  position: relative;
  z-index: 1000;
  @media only screen and (max-width: 711px) {
    /* position: ${({ isActive }) => (isActive ? 'fixed' : 'relative')};
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    overscroll-behavior: contain;
    padding-bottom: ${({ isActive }) => (isActive ? '100vh' : '100vh')}; */
  }
`

const _MapBoxMap = styled.div.attrs({ id: 'map' })<{ isActive: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  max-height: 80vh;
  font-size: 10px;
  @media only screen and (max-width: 711px) {
    max-height: 80vh;
    height: ${({ isActive }) => (isActive ? '100vh' : '100%')};
    margin-left: -20px;
    width: calc(100% + 40px);
  }
  .mapboxgl-ctrl-bottom-left {
    right: 10px;
    bottom: 2px;
    left: unset;
    @media only screen and (max-width: 711px) {
      pointer-events: none;
    }
  }
  /* .mapboxgl-ctrl-attrib-inner {
    * {
      color: var(--brandblue-bright-1) !important;
      background-color: var(--site-backgorund) !important;
    }
    color: var(--brandblue-bright-1) !important;
    background-color: var(--site-backgorund) !important;
  } */
  .mapboxgl-ctrl.mapboxgl-ctrl-attrib {
    background-color: var(--site-backgorund) !important;    
  }
  .mapboxgl-ctrl-bottom-right {
    bottom: 10px;
    right: 110px;
    @media only screen and (max-width: 711px) {
      pointer-events: none;
      bottom: 0px;
      right: 100px;
    }
  }
  .mapboxgl-popup-content {
    font-size: 14px;
    line-height: 1.4em;
    font-family: var(--font-sans-serif);
    font-weight: 500;
    .row {
      width: 100%;
    }
    label {
      /* font-size: 12px; */
      font-weight: 400;
      margin-right: 1em;
    }
  }
`
